"use client";

import type { ProductDetailEventData } from "@commerce-frontend/gtm-ecommerce";
import type { Locale } from "@commerce-frontend/i18n/types";
import { Container } from "@commerce-frontend/ui/base/Container/Container";
import { Section } from "@commerce-frontend/ui/base/Section/Section";
import type { ResultOf } from "@graphql-typed-document-node/core";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { AddToCartProvider } from "~/components/AddToCartContext";
import { Breadcrumbs } from "~/components/Breadcrumbs/Breadcrumbs";
import { GtmPageLoadEvent } from "~/components/GoogleAnalytics/GTMPageLoadEvent";
import { Image, imagePlaceholder } from "~/components/Image/Image";
import { ProductImageSlider } from "~/components/ProductImageSlider/ProductImageSlider";
import { useInsights } from "~/lib/insights-provider";
import { ProductDetailPageJsonLd } from "~/lib/jsonLd";
import { useNavigation } from "~/lib/navigation-provider";
import {
	deviceData,
	ecommerceData,
	locationData,
	sendGTMEvent,
	userData,
} from "~/lib/send-gtm-event";
import { useStoreConfig } from "~/lib/store-config/context";
import { ProductAddErrorToast } from "./_components/ProductAddErrorToast/ProductAddErrorToast";
import { ProductAddedToCartModalIntegration } from "./_components/ProductAddedToCartModal/ProductAddedToCartModalIntegration";
import { ProductBuySection } from "./_components/ProductBuySection/ProductBuySection";
import { ProductInformationSection } from "./_components/ProductInformationSection/ProductInformationSection";
import { ProductReviewsSection } from "./_components/ProductReviewsSection/ProductReviewsSection";
import type { ProductDetailPageFragment } from "./fragment";

type Props = {
	product: ResultOf<typeof ProductDetailPageFragment>;
	locale: Locale;
	initialSku: string | undefined;
};

export function ProductDetailPageView({ product, locale, initialSku }: Props) {
	const storeConfig = useStoreConfig();
	const t = useTranslations("ProductDetailPageView");
	const { resetQueryID } = useInsights();
	const { previousUrl, previousPathname } = useNavigation();
	let sku = initialSku;

	const { ref, inView } = useInView({
		threshold: 0,
		triggerOnce: true,
	});

	// Set sku to undefined when an invalid sku is requested
	const validSkus = product.variants.map((x) => x.sku);
	if (sku && !validSkus.includes(sku)) {
		sku = undefined;
	}

	// Set sku to the first variant if the product has only one variant
	if (product.variants?.length === 1 && product?.variants[0]?.sku) {
		sku = product.variants[0].sku;
	}

	const ratingSku = sku ?? product.primaryVariant.sku ?? undefined;

	// The base variant used to show product images and description
	// even if no specific variant (size) is selected
	const baseVariant =
		product.variants.find((v) => v.sku === product.primaryVariant?.sku) ?? product.variants[0];

	useEffect(() => {
		// We only want to include the queryID for Algolia Insights
		// if the user arrived from the search page
		if (previousPathname !== "/search") {
			resetQueryID();
		}
	}, []);

	useEffect(() => {
		sendGTMEvent<ProductDetailEventData>({
			event: "product_detail",
			timestamp: Date.now(),
			device: deviceData(),
			user: userData(),
			location: locationData({
				locale,
				pageType: "category",
				pageSubtype: "productdetail",
				productId: product.id,
				categoryId: product.primaryCategory?.id,
				previousUrl,
			}),
			ecommerce: ecommerceData([
				{
					product,
					sku,
				},
			]),
		});
	}, [product, sku, locale, previousUrl]);

	if (!baseVariant) {
		return null;
	}

	return (
		<>
			<GtmPageLoadEvent
				pageType="category"
				pageSubtype="productdetail"
				productId={product.id}
				categoryId={product.primaryCategory?.id}
			/>
			<ProductDetailPageJsonLd product={product} variant={baseVariant} locale={locale} />

			<Section spacing="bottom">
				<Container>
					{product.breadcrumbs ? (
						<Breadcrumbs locale={locale} breadcrumbs={product.breadcrumbs} />
					) : undefined}
					<div className="flex w-full flex-wrap">
						<div className="w-full lg:w-1/2">
							{/* Product image */}
							<section
								className="4xl:-ml-4xl 4xl:w-[calc(100%+2rem)] relative h-fit w-full lg:top-0 lg:pr-4xl lg:pt-9xl"
								aria-label={t("image-gallery")}
							>
								{baseVariant.images?.length ? (
									<ProductImageSlider images={baseVariant.images} />
								) : (
									<Image
										src={imagePlaceholder}
										alt={product.name}
										width={544}
										height={680}
										className="w-full"
									/>
								)}
							</section>
						</div>

						<div className="sticky top-0 w-full lg:w-1/2">
							<AddToCartProvider>
								<ProductBuySection initialSku={sku} product={product} locale={locale} />
								<ProductAddErrorToast />
								<ProductAddedToCartModalIntegration locale={locale} />
							</AddToCartProvider>
						</div>
					</div>
				</Container>
			</Section>
			<ProductInformationSection baseVariant={baseVariant} product={product} />
			<div id="product-reviews" ref={ref}>
				{storeConfig.etrustedEnabled && inView && product.reviews?.averageRating && ratingSku ? (
					<ProductReviewsSection slug={product.slug} locale={locale} />
				) : null}
			</div>
		</>
	);
}
